import {
  Button,
  Card,
  EmptyState,
  Field,
  SelectField,
  StatusPill,
  type StatusTone,
} from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useAgencyToday } from '../../lib/agency-day';
import { useApi } from '../../lib/api-hooks';

/**
 * Authorizations and payer routing, from the client's own page (§12, §18.2).
 *
 * Neither was reachable. `GET`/`POST /v1/authorizations` and
 * `GET`/`PUT /v1/clients/:id/payer-rules` all worked, all had tests, and the
 * Authorizations tab rendered the coverage panel — a view of something nobody
 * could set. An agency could be shown what dual-payer routing looks like and
 * had no way to configure it outside a `curl`.
 *
 * ## The two halves have different permissions, and the tab shows it
 *
 * §10.4 puts `auth:read` / `auth:create` on **BILLER alone**, while
 * `client:update` — which governs payer rules — belongs to CARE_MANAGER. That
 * is the deliberate asymmetry the spec describes: a biller sets up the payer
 * and the authorization; deciding which payer covers this client's Tuesday
 * mornings is care administration.
 *
 * So the tab is two panels with two gates rather than one screen behind one
 * permission. A care manager sees coverage and routing; a biller sees coverage
 * and authorizations. Neither sees a control that would answer them 403.
 */

const PERIOD_TYPES = ['DAILY', 'WEEKLY', 'MONTHLY', 'TOTAL'] as const;
const DECISIONS = ['PENDING', 'ACTIVE', 'CANCELLED'] as const;

const STATUS_TONE: Record<string, StatusTone> = {
  ACTIVE: 'verified',
  PENDING: 'progress',
  EXPIRED: 'inactive',
  EXHAUSTED: 'warning',
  CANCELLED: 'inactive',
};

interface Authorization {
  id: string;
  authNumber: string;
  payerId: string;
  serviceCodeId: string;
  authorizedUnits: number;
  remainingUnits: number;
  consumedPct: number;
  alertRaised: boolean;
  periodType: string;
  startDate: string;
  endDate: string;
  status: string;
}

interface PayerRef {
  id: string;
  name: string;
}

interface ServiceCodeRef {
  id: string;
  payerId: string;
  code: string;
  description: string | null;
}

/** The tenant's payers and service codes, for the pickers on both panels. */
function useCatalogue(enabled: boolean): {
  payers: PayerRef[];
  serviceCodes: ServiceCodeRef[];
  loading: boolean;
} {
  const api = useApi();

  const payers = useQuery({
    queryKey: ['payers', 'options'],
    queryFn: () => api.get<{ payers: PayerRef[] }>('/v1/payers'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const serviceCodes = useQuery({
    queryKey: ['service-codes', 'options'],
    queryFn: () => api.get<{ serviceCodes: ServiceCodeRef[] }>('/v1/service-codes'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    payers: payers.data?.payers ?? [],
    serviceCodes: serviceCodes.data?.serviceCodes ?? [],
    loading: payers.isPending || serviceCodes.isPending,
  };
}

const codesFor = (codes: ServiceCodeRef[], payerId: string): ServiceCodeRef[] =>
  codes.filter((code) => code.payerId === payerId);

// ---------------------------------------------------------------------------
// Authorizations — BILLER
// ---------------------------------------------------------------------------

export function AuthorizationsPanel({
  clientId,
  canRead,
  canCreate,
  canUpdate,
}: {
  clientId: string;
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Authorization | null>(null);

  const list = useQuery({
    queryKey: ['authorizations', clientId],
    queryFn: () =>
      api.get<{ authorizations: Authorization[] }>(`/v1/authorizations?clientId=${clientId}`),
    enabled: canRead,
  });

  // Nothing at all rather than an empty panel: a reader without `auth:read`
  // is not being kept from a feature, they are simply not the person who
  // manages authorizations. §12 hides rather than disables, for the same
  // reason a greyed-out Revenue section leaks an organisational fact.
  if (!canRead) return null;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Authorizations</h2>
          <p className="text-ink-3 mt-1 text-sm">
            What a payer has approved, and how much of it is left. Status is worked out when you
            look, so an authorization that ended yesterday reads expired without anything having
            run.
          </p>
        </div>

        {canCreate && !adding && (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            Record an authorization
          </Button>
        )}
      </div>

      <ErrorNote
        error={list.error}
        nextStep="Reload the page, or ask a biller to check the payer setup."
      />

      {adding && (
        <AuthorizationForm
          clientId={clientId}
          onDone={async () => {
            await refreshAfterWrite(queryClient, clientId);
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {editing && (
        <AuthorizationCorrectionForm
          authorization={editing}
          onDone={async () => {
            await refreshAfterWrite(queryClient, clientId);
            setEditing(null);
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {list.isPending ? (
        <p className="text-ink-3 py-6 text-center text-sm">Loading authorizations…</p>
      ) : (list.data?.authorizations ?? []).length === 0 ? (
        <div className="mt-4">
          <EmptyState
            title="No authorizations yet"
            explanation="A payer's approval to deliver a number of units over a period. Without one, visits for this client are private pay or unbilled."
          />
        </div>
      ) : (
        <ul className="mt-4 grid gap-3" data-testid="authorization-list">
          {(list.data?.authorizations ?? []).map((authorization) => (
            <li
              key={authorization.id}
              className="border-line flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div>
                <p className="text-ink text-sm font-semibold">{authorization.authNumber}</p>
                <p className="text-ink-3 text-xs">
                  {authorization.startDate} to {authorization.endDate} ·{' '}
                  {authorization.periodType.toLowerCase()}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-ink-2 text-sm">
                  {authorization.remainingUnits} of {authorization.authorizedUnits} units left
                </span>
                <StatusPill tone={STATUS_TONE[authorization.status] ?? 'inactive'}>
                  {authorization.status.charAt(0) +
                    authorization.status.slice(1).toLowerCase().replace('_', ' ')}
                </StatusPill>

                {canUpdate && !editing && (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(authorization)}>
                    Correct
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Both writes make the same three things stale. */
async function refreshAfterWrite(
  queryClient: ReturnType<typeof useQueryClient>,
  clientId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['authorizations', clientId] }),
    // Coverage reads authorizations for its caps and its remaining units, so it
    // goes stale the moment one is written.
    queryClient.invalidateQueries({ queryKey: ['clients', 'coverage', clientId] }),
    queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] }),
  ]);
}

/**
 * Correcting an authorization already on file (slice-02 C2).
 *
 * `PATCH /v1/authorizations/:id` shipped in slice 1 with tests and no way in,
 * so an authorization could be recorded and never corrected — and both things
 * that go wrong with one are ordinary. An auth number arrives transposed from a
 * fax; a payer extends an end date by a month. Neither is worth a developer and
 * a database write, which is what it cost until now.
 *
 * ## What is not editable, and why
 *
 * The client, the payer, the service code, the period type and the start date
 * are absent from `updateAuthorizationSchema` and absent here. Changing any of
 * them does not correct an authorization — it makes it a different one, against
 * different consumption, with usage rows already counted under the old shape.
 * The honest operation for that is to cancel this authorization and record the
 * real one, which leaves both on the record where an auditor can see what
 * happened.
 *
 * `authorizedUnits` and `unitsPerPeriod` are editable, because a payer revising
 * an approval upward is the single most common correction and the alternative
 * is a second authorization that double-counts the period.
 */
function AuthorizationCorrectionForm({
  authorization,
  onDone,
  onCancel,
}: {
  authorization: Authorization;
  onDone: () => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const api = useApi();

  const [authNumber, setAuthNumber] = useState(authorization.authNumber);
  const [units, setUnits] = useState(String(authorization.authorizedUnits));
  const [endDate, setEndDate] = useState(authorization.endDate.slice(0, 10));
  const [decision, setDecision] = useState<(typeof DECISIONS)[number]>(
    // The derived status may read EXPIRED or EXHAUSTED, which are not decisions
    // a person can assert (ADR-019). Fall back to the one they can.
    (DECISIONS as readonly string[]).includes(authorization.status)
      ? (authorization.status as (typeof DECISIONS)[number])
      : 'ACTIVE',
  );
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      await api.send('PATCH', `/v1/authorizations/${authorization.id}`, {
        authNumber: authNumber.trim(),
        authorizedUnits: Number(units),
        endDate,
        status: decision,
      });

      await onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border-line bg-surface-2 mt-4 grid gap-3 rounded-md border p-4"
      data-testid="authorization-correction"
    >
      <p className="text-ink-3 text-sm">
        Correcting {authorization.authNumber}. To move this to a different payer or service code,
        cancel it and record the right one — both stay on the record.
      </p>

      <ErrorNote error={failure} nextStep="Check the number and the dates, then try again." />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Authorization number"
          value={authNumber}
          onChange={(event) => setAuthNumber(event.target.value)}
        />
        <Field
          label="Units authorized"
          type="number"
          min={1}
          value={units}
          onChange={(event) => setUnits(event.target.value)}
        />
        <Field
          label="End date"
          type="date"
          hint="Extending it is the usual correction."
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
        />
        <SelectField
          label="Payer decision"
          hint="Expiry and exhaustion are worked out; this is what the payer said."
          value={decision}
          onChange={(event) => setDecision(event.target.value as (typeof DECISIONS)[number])}
        >
          {DECISIONS.map((value) => (
            <option key={value} value={value}>
              {value.charAt(0) + value.slice(1).toLowerCase()}
            </option>
          ))}
        </SelectField>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => void save()}
          pending={saving}
          {...(authNumber.trim() && Number(units) > 0 && endDate
            ? {}
            : ({
                disabled: true,
                disabledReason: 'A number, a unit figure and an end date.',
              } as const))}
        >
          Save the correction
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function AuthorizationForm({
  clientId,
  onDone,
  onCancel,
}: {
  clientId: string;
  onDone: () => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const api = useApi();
  const { payers, serviceCodes } = useCatalogue(true);
  // The agency's day, not the browser's UTC one (R-1e). Empty only before the
  // session has loaded, and the field is required, so nothing saves on a guess.
  const today = useAgencyToday() ?? '';

  const [payerId, setPayerId] = useState('');
  const [serviceCodeId, setServiceCodeId] = useState('');
  const [authNumber, setAuthNumber] = useState('');
  const [units, setUnits] = useState('');
  const [unitsPerPeriod, setUnitsPerPeriod] = useState('');
  const [periodType, setPeriodType] = useState<(typeof PERIOD_TYPES)[number]>('WEEKLY');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [decision, setDecision] = useState<(typeof DECISIONS)[number]>('ACTIVE');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const codes = codesFor(serviceCodes, payerId);

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      await api.send('POST', '/v1/authorizations', {
        clientId,
        payerId,
        serviceCodeId,
        authNumber: authNumber.trim(),
        authorizedUnits: Number(units),
        // A period cap with no period figure mis-caps every visit in the
        // period, so the contract refuses it. TOTAL is the one shape that has
        // no per-period figure to give.
        ...(periodType === 'TOTAL' ? {} : { unitsPerPeriod: Number(unitsPerPeriod) }),
        periodType,
        startDate,
        endDate,
        status: decision,
      });

      await onDone();
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const ready =
    payerId &&
    serviceCodeId &&
    authNumber.trim() &&
    Number(units) > 0 &&
    endDate &&
    (periodType === 'TOTAL' || Number(unitsPerPeriod) > 0);

  return (
    <div
      className="border-line bg-surface-2 mt-4 grid gap-3 rounded-md border p-4"
      data-testid="authorization-form"
    >
      <ErrorNote error={failure} nextStep="Check the dates and the unit figures, then try again." />

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Payer"
          value={payerId}
          onChange={(event) => {
            setPayerId(event.target.value);
            // The service code belongs to a payer, so changing the payer
            // invalidates the choice rather than leaving a mismatched pair the
            // API would refuse.
            setServiceCodeId('');
          }}
          hint={payers.length === 0 ? 'No payers set up yet. A biller adds them first.' : undefined}
        >
          <option value="">Choose a payer</option>
          {payers.map((payer) => (
            <option key={payer.id} value={payer.id}>
              {payer.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Service code"
          value={serviceCodeId}
          onChange={(event) => setServiceCodeId(event.target.value)}
          hint={payerId && codes.length === 0 ? 'This payer has no service codes yet.' : undefined}
        >
          <option value="">Choose a service code</option>
          {codes.map((code) => (
            <option key={code.id} value={code.id}>
              {code.code}
              {code.description ? ` — ${code.description}` : ''}
            </option>
          ))}
        </SelectField>

        <Field
          label="Authorization number"
          hint="As the payer issued it."
          value={authNumber}
          onChange={(event) => setAuthNumber(event.target.value)}
        />

        <SelectField
          label="Period"
          value={periodType}
          onChange={(event) => setPeriodType(event.target.value as (typeof PERIOD_TYPES)[number])}
        >
          {PERIOD_TYPES.map((period) => (
            <option key={period} value={period}>
              {period.charAt(0) + period.slice(1).toLowerCase()}
            </option>
          ))}
        </SelectField>

        <Field
          label="Units authorized"
          type="number"
          min={1}
          hint="The whole authorization, across every period."
          value={units}
          onChange={(event) => setUnits(event.target.value)}
        />

        {periodType !== 'TOTAL' && (
          <Field
            label="Units per period"
            type="number"
            min={1}
            hint="The cap inside one period. Required — a period cap with no figure mis-caps every visit."
            value={unitsPerPeriod}
            onChange={(event) => setUnitsPerPeriod(event.target.value)}
          />
        )}

        <Field
          label="Start date"
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />

        <Field
          label="End date"
          type="date"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
        />

        <SelectField
          label="Payer decision"
          // The stored column, which is the human decision. What the client
          // reads back is derived from it plus the dates and the consumption —
          // ADR-019 — so this is not "status", it is what the payer said.
          hint="What the payer has told you. Expiry and exhaustion are worked out from the dates and the units."
          value={decision}
          onChange={(event) => setDecision(event.target.value as (typeof DECISIONS)[number])}
        >
          {DECISIONS.map((value) => (
            <option key={value} value={value}>
              {value.charAt(0) + value.slice(1).toLowerCase()}
            </option>
          ))}
        </SelectField>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => void save()}
          pending={saving}
          {...(ready
            ? {}
            : ({
                disabled: true,
                disabledReason:
                  'Choose a payer and service code, and fill in the numbers and dates.',
              } as const))}
        >
          Save authorization
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payer rules — CARE_MANAGER
// ---------------------------------------------------------------------------

const DAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

interface RuleDraft {
  payerId: string;
  serviceCodeId: string;
  priority: number;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  effectiveFrom: string;
  effectiveTo: string;
}

/**
 * Which payer absorbs a visit's minutes, and in what order (§18.2).
 *
 * A `PUT` of the whole ordered set rather than per-rule edits, because the
 * rules are a **sequence** whose meaning is positional: adding one at priority
 * 5 changes what the rule at 10 absorbs. Editing them one at a time would leave
 * the client briefly in a state nobody designed, and that window is exactly
 * when a visit might be allocated.
 */
export function PayerRulesEditor({
  clientId,
  canEdit,
}: {
  clientId: string;
  canEdit: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const { payers, serviceCodes } = useCatalogue(true);
  const today = useAgencyToday() ?? '';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RuleDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const rules = useQuery({
    queryKey: ['clients', 'payer-rules', clientId],
    queryFn: () =>
      api.get<{
        rules: {
          id: string;
          payerId: string;
          serviceCodeId: string;
          priority: number;
          appliesWhen: { daysOfWeek?: number[]; startTime?: string; endTime?: string };
          effectiveFrom: string;
          effectiveTo: string | null;
        }[];
        defaultPayerId: string | null;
      }>(`/v1/clients/${clientId}/payer-rules`),
  });

  const payerName = (id: string): string =>
    payers.find((p) => p.id === id)?.name ?? 'Unknown payer';
  const codeLabel = (id: string): string => serviceCodes.find((c) => c.id === id)?.code ?? '';

  const startEditing = (): void => {
    setDraft(
      (rules.data?.rules ?? []).map((rule) => ({
        payerId: rule.payerId,
        serviceCodeId: rule.serviceCodeId,
        priority: rule.priority,
        daysOfWeek: rule.appliesWhen.daysOfWeek ?? [],
        startTime: rule.appliesWhen.startTime ?? '',
        endTime: rule.appliesWhen.endTime ?? '',
        effectiveFrom: rule.effectiveFrom,
        effectiveTo: rule.effectiveTo ?? '',
      })),
    );
    setFailure(null);
    setEditing(true);
  };

  const update = (index: number, patch: Partial<RuleDraft>): void =>
    setDraft((was) => was.map((rule, at) => (at === index ? { ...rule, ...patch } : rule)));

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      await api.send('PUT', `/v1/clients/${clientId}/payer-rules`, {
        rules: draft
          .filter((rule) => rule.payerId && rule.serviceCodeId)
          .map((rule) => ({
            payerId: rule.payerId,
            serviceCodeId: rule.serviceCodeId,
            priority: rule.priority,
            appliesWhen: {
              // An empty condition means "every visit", so the keys are
              // omitted rather than sent empty — `appliesWhen: {}` is the
              // shape `describeCondition` renders as "Every visit".
              ...(rule.daysOfWeek.length > 0 ? { daysOfWeek: [...rule.daysOfWeek].sort() } : {}),
              ...(rule.startTime ? { startTime: rule.startTime } : {}),
              ...(rule.endTime ? { endTime: rule.endTime } : {}),
            },
            effectiveFrom: rule.effectiveFrom,
            ...(rule.effectiveTo ? { effectiveTo: rule.effectiveTo } : {}),
          })),
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients', 'payer-rules', clientId] }),
        queryClient.invalidateQueries({ queryKey: ['clients', 'coverage', clientId] }),
        queryClient.invalidateQueries({ queryKey: ['clients', 'list'] }),
      ]);

      setEditing(false);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const recorded = rules.data?.rules ?? [];

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Payer routing</h2>
          <p className="text-ink-3 mt-1 text-sm">
            Which payer absorbs a visit first, and what is left over for the fall-through. The
            coverage panel above is the answer; this is where it comes from.
          </p>
        </div>

        {canEdit && !editing && (
          <Button variant="secondary" size="sm" onClick={startEditing}>
            {recorded.length === 0 ? 'Set up routing' : 'Edit routing'}
          </Button>
        )}
      </div>

      <ErrorNote error={rules.error} nextStep="Reload the page and try again." />

      {editing ? (
        <div className="mt-4 grid gap-4" data-testid="payer-rules-editor">
          <ErrorNote
            error={failure}
            nextStep="Give each rule its own priority, and check the dates."
          />

          {draft.map((rule, index) => {
            const codes = codesFor(serviceCodes, rule.payerId);

            return (
              <fieldset key={index} className="border-line grid gap-3 rounded-md border p-3">
                <legend className="text-ink-3 px-1 text-xs">Rule {index + 1}</legend>

                <div className="grid gap-3 sm:grid-cols-2">
                  <SelectField
                    label="Payer"
                    value={rule.payerId}
                    onChange={(event) =>
                      update(index, { payerId: event.target.value, serviceCodeId: '' })
                    }
                  >
                    <option value="">Choose a payer</option>
                    {payers.map((payer) => (
                      <option key={payer.id} value={payer.id}>
                        {payer.name}
                      </option>
                    ))}
                  </SelectField>

                  <SelectField
                    label="Service code"
                    value={rule.serviceCodeId}
                    onChange={(event) => update(index, { serviceCodeId: event.target.value })}
                  >
                    <option value="">Choose a service code</option>
                    {codes.map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.code}
                      </option>
                    ))}
                  </SelectField>

                  <Field
                    label="Priority"
                    type="number"
                    min={0}
                    hint="Lower absorbs first. Two rules cannot share one."
                    value={String(rule.priority)}
                    onChange={(event) => update(index, { priority: Number(event.target.value) })}
                  />

                  <Field
                    label="Effective from"
                    type="date"
                    value={rule.effectiveFrom}
                    onChange={(event) => update(index, { effectiveFrom: event.target.value })}
                  />

                  <Field
                    label="Effective to"
                    type="date"
                    hint="Leave blank for open-ended."
                    value={rule.effectiveTo}
                    onChange={(event) => update(index, { effectiveTo: event.target.value })}
                  />
                </div>

                <fieldset className="grid gap-2">
                  <legend className="text-ink text-sm font-medium">Applies on</legend>
                  <p className="text-ink-3 text-xs">Leave every day unticked for every visit.</p>
                  <div className="flex flex-wrap gap-3">
                    {DAYS.map((day) => (
                      <label
                        key={day.value}
                        className="text-ink-2 flex items-center gap-1.5 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={rule.daysOfWeek.includes(day.value)}
                          onChange={(event) =>
                            update(index, {
                              daysOfWeek: event.target.checked
                                ? [...rule.daysOfWeek, day.value]
                                : rule.daysOfWeek.filter((value) => value !== day.value),
                            })
                          }
                        />
                        {day.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label="Not before"
                    type="time"
                    value={rule.startTime}
                    onChange={(event) => update(index, { startTime: event.target.value })}
                  />
                  <Field
                    label="Not after"
                    type="time"
                    value={rule.endTime}
                    onChange={(event) => update(index, { endTime: event.target.value })}
                  />
                </div>

                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDraft((was) => was.filter((_, at) => at !== index))}
                  >
                    Remove this rule
                  </Button>
                </div>
              </fieldset>
            );
          })}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setDraft((was) => [
                  ...was,
                  {
                    payerId: '',
                    serviceCodeId: '',
                    // Ten apart, so a rule can be slotted between two without
                    // renumbering the rest.
                    priority: was.length === 0 ? 10 : Math.max(...was.map((r) => r.priority)) + 10,
                    daysOfWeek: [],
                    startTime: '',
                    endTime: '',
                    effectiveFrom: today,
                    effectiveTo: '',
                  },
                ])
              }
            >
              Add a rule
            </Button>
            <span className="flex-1" />
            <Button onClick={() => void save()} pending={saving}>
              Save routing
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : recorded.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            title="No routing rules"
            explanation="Every visit falls through to the client's default payer. Add a rule when a payer covers only part of the week, or only up to a cap."
          />
        </div>
      ) : (
        <ol className="mt-4 grid gap-2" data-testid="payer-rules-list">
          {recorded.map((rule) => (
            <li
              key={rule.id}
              className="border-line flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div>
                <p className="text-ink text-sm font-semibold">{payerName(rule.payerId)}</p>
                <p className="text-ink-3 text-xs">
                  {codeLabel(rule.serviceCodeId)} · from {rule.effectiveFrom}
                  {rule.effectiveTo ? ` to ${rule.effectiveTo}` : ''}
                </p>
              </div>
              <span className="text-ink-2 text-sm">Priority {rule.priority}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
